// SSE consumer for /api/admin/contents/:p/:s/ai-rewrite. Returns an async
// iterable so the caller can `for await` and react to tokens / done / error.
// EventSource doesn't support POST, so we use fetch + ReadableStream.
import type { AiChatTurn } from "@shared/schemas";
import { aiRateLimitMessage, asRateLimitPayload } from "./rate-limit-message";
import { apiErrorMessage } from "./api-error-message";
import {
  redirectIfUnauthorized,
  resolveAdminApiUrl,
} from "./platform-admin";

export type AiStreamEvent =
  | { kind: "meta"; provider: string; requestId: string }
  | { kind: "mode"; mode: "chat" | "edit" }
  | { kind: "token"; text: string }
  | {
      kind: "done";
      finishReason?: string;
      usage?: { input?: number; output?: number };
    }
  | { kind: "error"; code: string; message: string };

export async function* streamAiRewrite(args: {
  projectIdOrSlug: string;
  slug: string;
  body: string;
  history: AiChatTurn[];
  userMessage: string;
  signal?: AbortSignal;
}): AsyncIterable<AiStreamEvent> {
  const res = await fetch(
    resolveAdminApiUrl(
      `/api/admin/contents/${encodeURIComponent(args.projectIdOrSlug)}/${encodeURIComponent(args.slug)}/ai-rewrite`,
    ),
    {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        body: args.body,
        history: args.history,
        userMessage: args.userMessage,
      }),
      signal: args.signal,
    },
  );
  if (res.status === 401) {
    redirectIfUnauthorized();
    yield { kind: "error", code: "unauthorized", message: "sign in required" };
    return;
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    yield {
      kind: "error",
      code: res.status === 503 ? "ai_unavailable" : `http_${res.status}`,
      // The http_4xx code shape is load-bearing (use-chat-stream drops the
      // optimistic user turn for 4xx), so only the message is specialized.
      message:
        res.status === 429 ? rateLimitMessage(text) : apiErrorMessage(res.status, parseJson(text)),
    };
    return;
  }
  if (!res.body) {
    yield { kind: "error", code: "no_body", message: "no response body" };
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // SSE frames are separated by \n\n. Keep the trailing partial frame.
      const frames = buffer.split("\n\n");
      buffer = frames.pop() ?? "";
      for (const frame of frames) {
        const ev = parseSseFrame(frame);
        if (ev) yield ev;
      }
    }
  } catch (e) {
    if ((e as Error).name === "AbortError") return;
    yield {
      kind: "error",
      code: "stream_error",
      message: (e as Error).message ?? "stream failed",
    };
  } finally {
    // Tear down the HTTP body when the consumer exits early (e.g. an
    // event:error mid-stream makes the caller break out of its for-await) —
    // releaseLock alone would leave the connection draining until GC.
    void reader.cancel().catch(() => {
      /* noop */
    });
    try {
      reader.releaseLock();
    } catch {
      /* noop */
    }
  }
}

// 429 arrives as a plain JSON body (the SSE stream never starts). Render it as
// prose so the chat panel doesn't show the user raw JSON.
function rateLimitMessage(body: string): string {
  try {
    return aiRateLimitMessage(asRateLimitPayload(JSON.parse(body)));
  } catch {
    return aiRateLimitMessage({});
  }
}

function parseSseFrame(frame: string): AiStreamEvent | null {
  const lines = frame.split("\n");
  let event = "message";
  const dataLines: string[] = [];
  for (const line of lines) {
    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      const value = line.slice(5);
      dataLines.push(value.startsWith(" ") ? value.slice(1) : value);
    }
  }
  const data = dataLines.join("\n");
  if (!data) return null;
  try {
    const obj = JSON.parse(data);
    if (event === "meta") {
      return { kind: "meta", provider: obj.provider, requestId: obj.requestId };
    }
    if (event === "mode") {
      const mode = obj.mode === "edit" ? "edit" : "chat";
      return { kind: "mode", mode };
    }
    if (event === "token") return { kind: "token", text: obj.text ?? "" };
    if (event === "done") {
      return { kind: "done", finishReason: obj.finishReason, usage: obj.usage };
    }
    if (event === "error") {
      return { kind: "error", code: obj.code ?? "unknown", message: obj.message ?? "" };
    }
  } catch {
    /* ignore malformed frame */
  }
  return null;
}

// Error bodies are JSON from our API, but a proxy / edge failure can hand back
// HTML or plain text; apiErrorMessage falls back on the status for those.
function parseJson(text: string): unknown {
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return null;
  }
}
